/**
 * MOCK/TEMPORARY — the song library.
 *
 * Songs live in the client-side songsStore; this module is the seam that store
 * reads through. Nothing here persists across a reload, which is the current,
 * expected behaviour.
 *
 * Note the shape of these functions: they take and return plain domain objects
 * and never touch React state. When they become real `fetch` calls the store
 * code above them does not change.
 *
 * Real endpoints: docs/api-contract.md, "Songs" and "Song versions".
 */
import { USE_MOCK_API } from '../config';
import { shortDate } from '../lib/formatting';
import { newId } from '../lib/ids';
import type { Song, SongVersion, SongVersionKind } from '../types/domain';
import { ApiError } from './errors';
import { http } from './http';
import { assertMock, mockDelay } from './mock';
import {
  mockCatalogueSong,
  mockCatalogueSongs,
  mockCataloguePatch,
  mockCatalogueRemove,
  mockCatalogueUpsert,
} from './mockCatalogue';

/** The three counters this module owns, as opposed to the fields a caller may patch. */
type CounterField = 'playCount' | 'shareCount' | 'downloadCount';

/**
 * Adds one to a counter on the stored row.
 *
 * ============================================================================
 * THE INCREMENT IS THE MOCK'S, NOT THE CALLER'S, and that is the whole reason this helper
 * exists rather than the three endpoints taking a number. `recordPlay(id)` says "a play
 * happened"; the table decides what the total becomes. An endpoint that accepted
 * `{ playCount: 5000 }` would let any client write its own chart position, and Trending ranks
 * on exactly these fields.
 *
 * The client keeps its own optimistic copy in songsStore, which is why the two agree today
 * without either reading the other. They are two counts of the same events, and the day this
 * becomes a real request the server's is the one that survives.
 * ============================================================================
 */
function bumpCounter(id: string, field: CounterField): void {
  const song = mockCatalogueSong(id);
  if (!song) return;
  mockCataloguePatch(id, { [field]: (song[field] ?? 0) + 1 });
}

export const songsApi = {
  /**
   * MOCK: GET /songs
   *
   * Reads the mock's songs table rather than calling `createInitialSongs()` again. The
   * factory mints fresh ids on every call, so a second call answered with the same four
   * songs under DIFFERENT ids — which was harmless while nothing else consulted the mock's
   * copy, and stopped being harmless once the public reads did. See mockCatalogue.SEED.
   */
  async getSongs(): Promise<Song[]> {
    if (!USE_MOCK_API) {
      return http.get<Song[]>('/songs');
    }
    assertMock('songsApi.getSongs');
    return mockDelay(mockCatalogueSongs());
  },

  /** GET /songs/:id — one song by id, or null when the server has no such row. */
  async getSong(id: string): Promise<Song | null> {
    if (!USE_MOCK_API) {
      try {
        return await http.get<Song>(`/songs/${id}`);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    }
    assertMock('songsApi.getSong');
    return mockDelay(mockCatalogueSong(id));
  },

  /** GET /songs/:songId/versions/:versionId — the hidden full-song backing row for one version. */
  async getVersionSong(songId: string, versionId: string): Promise<Song | null> {
    if (!USE_MOCK_API) {
      try {
        return await http.get<Song>(`/songs/${songId}/versions/${versionId}`);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    }
    assertMock('songsApi.getVersionSong');
    const song = mockCatalogueSong(songId);
    const version = song?.versions.find((candidate) => candidate.id === versionId);
    return mockDelay(version?.kind === 'original' ? song : null);
  },

  /**
   * MOCK: PATCH /songs/:id
   *
   * WRITES THROUGH TO THE TABLE, which is what makes publishing visible to the public reads.
   * `songsStore.setVisibility` routes through here, and `discoveryApi.getShowcase` now
   * queries the table instead of being handed the store's library — so without this line a
   * song made discoverable in the Library would never appear on the Home page. The store's
   * own optimistic update is what moves the UI; this is the record changing.
   *
   * A real backend rejects `visibility` on this endpoint and requires
   * `PUT /songs/:id/visibility` instead, for the reasons docs/api-contract.md §6 gives. The
   * mock accepts it because `setVisibility` is the only writer and there is one account.
   */
  async updateSong(id: string, patch: Partial<Song>): Promise<Partial<Song> & { id: string }> {
    if (!USE_MOCK_API) {
      return http.patch<Partial<Song> & { id: string }>(`/songs/${id}`, patch);
    }
    assertMock('songsApi.updateSong');
    const current = mockCatalogueSong(id);
    const nextPatch =
      patch.visibility === 'public' && !current?.discoverableAt
        ? { ...patch, discoverableAt: new Date().toISOString() }
        : patch;
    mockCataloguePatch(id, nextPatch);
    return mockDelay({ ...nextPatch, id });
  },

  /** MOCK: DELETE /songs/:id — removes the row, so a deleted song leaves the catalogue too. */
  async deleteSong(id: string): Promise<void> {
    if (!USE_MOCK_API) {
      await http.delete<void>(`/songs/${id}`);
      return;
    }
    assertMock('songsApi.deleteSong');
    mockCatalogueRemove(id);
    return mockDelay(undefined);
  },

  /**
   * MOCK: POST /songs/:id/duplicate
   *
   * RETURNS THE CANONICAL COPY, the same way the real endpoint does. That is what the Library
   * needs now that a duplicate is something you can keep working on rather than a row that only
   * exists in local state: the caller inserts the returned song, and every later server-backed
   * read or write refers to the same id.
   *
   * The mock therefore duplicates the TABLE'S row rather than the caller fabricating one for
   * itself. This is still not persistence or authority — it is the same browser memory as the
   * rest of this file — but it gives the seam the real route's shape, which is the part that
   * matters to the store above it.
   */
  async duplicateSong(id: string): Promise<Song> {
    if (!USE_MOCK_API) {
      return http.post<Song>(`/songs/${id}/duplicate`);
    }
    assertMock('songsApi.duplicateSong');
    const source = mockCatalogueSong(id);
    if (!source) throw new ApiError(404, 'Song not found', 'not_found');
    const nextSeq = Math.max(0, ...mockCatalogueSongs().map((song) => song.seq)) + 1;
    const copy: Song = {
      ...source,
      id: newId(),
      seq: nextSeq,
      title: `${source.title} (copy)`,
      date: 'Just now',
      favorite: false,
      visibility: 'private',
      downloadCount: 0,
      playCount: undefined,
      loveCount: undefined,
      shareCount: undefined,
      versions: (source.versions ?? []).map((version) => ({ ...version, id: newId() })),
    };
    mockCatalogueUpsert(copy);
    return mockDelay(copy);
  },

  /**
   * MOCK: PUT /songs/:id/favorite
   *
   * Written through, because a Love is a RANKING SIGNAL and not only a star in the Library:
   * `lib/engagement.trendingScore` counts it, and the Trending list now comes from the table.
   * Without this line, Loving a published song would move the heart and leave the Home page's
   * order unchanged.
   */
  async setFavorite(id: string, favorite: boolean): Promise<void> {
    if (!USE_MOCK_API) {
      await http.request<void>(`/songs/${id}/favorite`, { method: 'PUT', body: { favorite } });
      return;
    }
    assertMock('songsApi.setFavorite');
    mockCataloguePatch(id, { favorite });
    return mockDelay(undefined);
  },

  /** PUT /songs/:id/love — the discoverable-song love path. */
  async setDiscoveryLove(id: string, loved: boolean): Promise<void> {
    if (!USE_MOCK_API) {
      await http.request<void>(`/songs/${id}/love`, { method: 'PUT', body: { loved } });
      return;
    }
    assertMock('songsApi.setDiscoveryLove');
    mockCataloguePatch(id, { favorite: loved });
    return mockDelay(undefined);
  },

  /**
   * MOCK: POST /songs/:id/versions
   *
   * The real endpoint creates the version server-side and returns it. Here the
   * caller supplies the display label because version numbering is derived from
   * the song's current version list, which only the store knows.
   */
  async createVersion(
    songId: string,
    label: string,
    length: string,
    kind: SongVersionKind,
    prompt?: string,
    audioUrl?: string | null,
    providerClipId?: string,
    createdAt?: string,
    lyrics?: string,
    lyricsTimedRaw?: Song['lyricsTimedRaw'],
    lyricTimings?: Song['lyricTimings'],
    lyricSectionTimings?: Song['lyricSectionTimings']
  ): Promise<SongVersion> {
    if (!USE_MOCK_API) {
      return http.post<SongVersion>(`/songs/${songId}/versions`, {
        label,
        length,
        kind,
        prompt,
        audioUrl,
        providerClipId,
        createdAt,
        lyrics,
        lyricsTimedRaw,
        lyricTimings,
        lyricSectionTimings,
      });
    }
    assertMock('songsApi.createVersion');
    const stampedAt = createdAt ?? new Date().toISOString();
    return mockDelay({
      id: newId(),
      label,
      kind,
      length,
      date: shortDate(stampedAt),
      createdAt: stampedAt,
      prompt,
      audioUrl,
      providerClipId,
      ...(lyrics !== undefined ? { lyrics } : null),
      ...(lyricsTimedRaw !== undefined ? { lyricsTimedRaw } : null),
      ...(lyricTimings !== undefined ? { lyricTimings } : null),
      ...(lyricSectionTimings !== undefined ? { lyricSectionTimings } : null),
    });
  },

  /** MOCK: DELETE /songs/:songId/versions/:versionId */
  async deleteVersion(songId: string, versionId: string): Promise<void> {
    if (!USE_MOCK_API) {
      await http.delete<void>(`/songs/${songId}/versions/${versionId}`);
      return;
    }
    assertMock('songsApi.deleteVersion');
    return mockDelay(undefined);
  },

  /** MOCK: PATCH /songs/:songId/versions/:versionId */
  async renameVersion(songId: string, versionId: string, label: string): Promise<void> {
    if (!USE_MOCK_API) {
      await http.patch<void>(`/songs/${songId}/versions/${versionId}`, { label });
      return;
    }
    assertMock('songsApi.renameVersion');
    return mockDelay(undefined);
  },

  /** MOCK: POST /songs/:songId/versions/:versionId/save */
  async saveVersionToLibrary(songId: string, versionId: string): Promise<Song> {
    if (!USE_MOCK_API) {
      return http.post<Song>(`/songs/${songId}/versions/${versionId}/save`);
    }
    assertMock('songsApi.saveVersionToLibrary');
    const song = mockCatalogueSong(songId);
    if (!song) throw new ApiError(404, 'Song not found', 'not_found');
    const version = song.versions.find((candidate) => candidate.id === versionId);
    if (!version) throw new ApiError(404, 'Version not found', 'not_found');
    const nextSeq = Math.max(0, ...mockCatalogueSongs().map((candidate) => candidate.seq)) + 1;
    const stampedAt = version.createdAt ?? new Date().toISOString();
    const savedId = newId();
    const copyVersionId = newId();
    const hasAudio = Boolean(version.audioUrl ?? song.audioUrl);
    const saved: Song = {
      ...song,
      id: savedId,
      seq: nextSeq,
      title: `${song.title} — ${version.label}`,
      length: version.length,
      date: shortDate(stampedAt),
      createdAt: stampedAt,
      lyrics: version.lyrics ?? song.lyrics,
      lyricsTimedRaw:
        version.lyricsTimedRaw !== undefined ? version.lyricsTimedRaw : song.lyricsTimedRaw,
      lyricTimings:
        version.lyricTimings !== undefined ? version.lyricTimings : song.lyricTimings,
      lyricSectionTimings:
        version.lyricSectionTimings !== undefined ? version.lyricSectionTimings : song.lyricSectionTimings,
      favorite: false,
      visibility: 'private',
      downloadCount: 0,
      playCount: undefined,
      loveCount: undefined,
      shareCount: undefined,
      prompt: version.prompt ?? song.prompt,
      creditsUsed: 0,
      audioUrl: hasAudio ? `/api/media/audio/${savedId}` : null,
      providerClipId: version.providerClipId ?? song.providerClipId,
      audioProvenance: version.audioProvenance ?? song.audioProvenance,
      versions: [
        {
          ...version,
          id: copyVersionId,
          label: 'Original',
          kind: 'original',
          date: shortDate(stampedAt),
          createdAt: stampedAt,
          audioUrl: hasAudio ? `/api/media/audio/${savedId}/versions/${copyVersionId}` : null,
          lyrics: version.lyrics ?? song.lyrics,
          lyricsTimedRaw:
            version.lyricsTimedRaw !== undefined ? version.lyricsTimedRaw : song.lyricsTimedRaw,
          lyricTimings:
            version.lyricTimings !== undefined ? version.lyricTimings : song.lyricTimings,
          lyricSectionTimings:
            version.lyricSectionTimings !== undefined ? version.lyricSectionTimings : song.lyricSectionTimings,
        },
      ],
      parentSongId: undefined,
    };
    mockCatalogueUpsert(saved);
    return mockDelay(saved);
  },

  /**
   * MOCK: POST /songs/:id/downloads
   *
   * Records that a download happened, for the per-song download counter. The
   * file itself is built client-side (see src/lib/download.ts) and is a text
   * placeholder, NOT audio — there is no audio to serve yet.
   */
  async recordDownload(id: string, format?: string): Promise<void> {
    if (!USE_MOCK_API) {
      await http.post<void>(`/songs/${id}/downloads`, format ? { format } : undefined);
      return;
    }
    assertMock('songsApi.recordDownload');
    bumpCounter(id, 'downloadCount');
    return mockDelay(undefined);
  },

  /**
   * MOCK: POST /songs/:id/plays
   *
   * Records that a run reached the app's play threshold, for the per-song play counter
   * Trending reads. The player calls this once the current run has accumulated at least
   * 30 listened seconds, so starting a track does not count immediately.
   *
   * THE COUNTER IS THE SERVER'S, AND THE MOCK NOW BEHAVES LIKE ONE: `bumpCounter` adds one
   * from the REQUEST rather than storing a total from the body, so a caller cannot post a
   * thousand plays in a field. That is the property the real endpoint has to keep, along with
   * the two it cannot get from here — rate limiting, and per-account accumulation. See
   * docs/api-contract.md §22.
   */
  async recordPlay(id: string): Promise<void> {
    if (!USE_MOCK_API) {
      await http.post<void>(`/songs/${id}/plays`);
      return;
    }
    assertMock('songsApi.recordPlay');
    bumpCounter(id, 'playCount');
    return mockDelay(undefined);
  },

  /**
   * MOCK: POST /songs/:id/shares
   *
   * Records that a share left the app — the sheet was used, or the link reached the
   * clipboard. Mirrors `recordPlay` exactly, including the part that matters: the counter is
   * incremented here, from the request, and never taken from the body.
   *
   * It counts a share being SENT, not a link being opened. Nothing here can know the
   * second, and there is no server behind the URL to find out.
   */
  async recordShare(id: string): Promise<void> {
    if (!USE_MOCK_API) {
      await http.post<void>(`/songs/${id}/shares`);
      return;
    }
    assertMock('songsApi.recordShare');
    bumpCounter(id, 'shareCount');
    return mockDelay(undefined);
  },
};
